import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  filter,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import {
  ActionPermission,
  apiLoginResponse,
  CheckTokenResponse,
  IActionPermissionManage,
  IActionPermissionResponse,
  IUserLogin,
  IUserProfileDetails,
} from '../../models/interface/login/i-login';
import { ILoginStatusEnum } from '../../models/enum/login-enum';
import { ToastrService } from 'ngx-toastr';
import { RolePermissionService } from '../role-permission/role-permission.service';
import { OrganizationOrgService } from '../organization-org/organization-org.service';
import { Router } from '@angular/router';
import { StatusCodeEnum } from '../../models/enum/status-code-enum';
import {
  childSubMenuIdMap,
  subMenuToChildIdsMap,
  sidebarMenus,
  allSubMenusToChildIdsMapRecord,
} from '../../../shared/constants/sidebar-menu.constant';
import { ApiResponse } from '../../models/interface/api-response/api-response';
import { DepartmentsCodeEnum } from '../../models/enum/departments-enum';
import {
  AssetManagementAction,
  GeneralManagementAction,
  ManagementTypeKey,
  SystemManagementAction,
} from '../../models/enum/role-permssion-access';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private LddAPIUrl: string = environment.url.lddApiUrl;
  private loginUrl: string = `${this.LddAPIUrl}${environment.url.authenticationUrl}`;
  private logOutUrl: string = `${this.LddAPIUrl}${environment.url.authenticationLogoutUrl}`;
  private actionPermissionsUrl: string = `${this.LddAPIUrl}${environment.url.actionPermissionsUrl}`;
  private apiCheckTokenUrl: string = environment.url.apiCheckToken;
  private currentUserLogin = new BehaviorSubject<IUserProfileDetails | null>(
    null,
  );
  private currentDefaultPath = new BehaviorSubject<string>('');
  private actionPermissionSubject =
    new BehaviorSubject<ActionPermission | null>(null);

  currentUserLogin$ = this.currentUserLogin.asObservable();
  currentDefaultPath$ = this.currentDefaultPath.asObservable();
  actionPermission$ = this.actionPermissionSubject.asObservable();

  private lddUrlBase = environment.url.lddServiceApiUrl;

  constructor(
    private http: HttpClient,
    private toast: ToastrService,
    private roleSrv: RolePermissionService,
    private orgService: OrganizationOrgService,
    private router: Router,
  ) {}

  checkTokenAuthenticated(
    currentToken: string,
    system_id: string,
    citizen_id: string,
  ): Observable<boolean> {
    sessionStorage.setItem('access_token', btoa(currentToken));
    sessionStorage.setItem('system_id', btoa(system_id));
    sessionStorage.setItem('info', btoa(citizen_id));
    const baseParams = new HttpParams()
      .set('token', currentToken.trim())
      .set('system_id', system_id.trim());
    return this.http
      .post<CheckTokenResponse>(this.apiCheckTokenUrl, baseParams)
      .pipe(
        take(1),
        map(
          (res: CheckTokenResponse) =>
            res.response.status === ILoginStatusEnum.SUCCESS,
        ),
      );
  }

  logout(): Observable<ApiResponse> {
    let token = this.getAccessToken();
    const body = { token: token };
    return this.http.post<ApiResponse>(this.logOutUrl, body);
  }

  private encodeToBase64Url(value: string): string {
    let base64 = btoa(value);
    base64 = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    return base64;
  }

  decodeFromBase64Url(base64Url: string): string {
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

    const padding = base64.length % 4;
    if (padding) {
      base64 += '='.repeat(4 - padding);
    }

    try {
      return atob(base64);
    } catch (error) {
      throw new Error('Invalid Base64 string');
    }
  }

  userLogin(
    citizenId: string,
    currentToken: string,
    system_id: string,
    citizen_id: string,
  ): Observable<boolean> {
    let getIdCard = citizenId ? citizenId.trim() : '';
    let getExistIdCard = citizen_id ? citizen_id.trim() : '';
    let currentId = getIdCard ? getIdCard : getExistIdCard;

    let body = { citizen_id: currentId };

    return this.http.post<apiLoginResponse>(this.loginUrl, body).pipe(
      switchMap((res: apiLoginResponse) => {
        const currentUser = res?.data as IUserLogin;
        if (currentUser && currentUser.token) {
          const encodedToken = this.encodeToBase64Url(currentUser.token);
          const encodedCurrentToken = this.encodeToBase64Url(currentToken);
          const encodedSystemId = this.encodeToBase64Url(system_id);
          const encodedCitizenId = this.encodeToBase64Url(currentId);

          sessionStorage.setItem('userToken', encodedToken);
          sessionStorage.setItem('access_token', encodedCurrentToken);
          sessionStorage.setItem('system_id', encodedSystemId);
          sessionStorage.setItem('info', encodedCitizenId);

          this.updateUserProfile(currentUser);

          return this.getActionPermissionsData$().pipe(
            map(() => true),
            catchError((err) => {
              return of(false);
            }),
          );
        } else {
          this.toast.error('ขออภัยเกิดข้อผิดพลาด', 'ตรวจสอบไม่พบข้อมูล', {
            timeOut: 2000,
            progressBar: true,
          });
          return of(false);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === StatusCodeEnum.NOT_FOUND) {
          this.router.navigateByUrl('/page-not-found');
        } else {
          this.toast.error('ขออภัยเกิดข้อผิดพลาด', 'ตรวจสอบไม่พบข้อมูล', {
            timeOut: 2000,
            progressBar: true,
          });
        }
        return of(false);
      }),
    );
  }

  updateUserProfile(userInfo: IUserLogin) {
    const thaiFullName = this.buildFullName(
      userInfo?.fname_th,
      userInfo?.lname_th,
    );
    const engFullName = this.buildFullName(
      userInfo?.fname_eng,
      userInfo?.lname_eng,
    );

    const { visibleSubMenuIds, visibleChildrenSubMenuIds } =
      this.getVisibleMenuIds(userInfo);

    let setDefaultPath = this.findFirstSidebarPathBySubMenuId(
      visibleSubMenuIds,
      visibleChildrenSubMenuIds,
    );
    this.currentDefaultPath.next(setDefaultPath);

    this.setUserProfile({
      user_id: userInfo.user_id,
      user_position: userInfo.position_name,
      prefix_name: userInfo.prefix_name,
      user_thai_name: thaiFullName,
      user_eng_name: engFullName,
      is_admin: userInfo.is_admin_only,
      agency_name: userInfo.agency_name,
      work_group_name: userInfo.work_group_name,
      agency_id: userInfo.agency_id,
      main_org_id: +userInfo.org_item.org_id,
      agency_type: userInfo.agency_type,
      agency_type_name: userInfo.agency_type_name,
      orgInfo: this.orgService.calculateOrder(
        userInfo.org_item,
        userInfo.agency_id,
      ),
      userPermissions: this.roleSrv.setUserRole(
        userInfo.access_rights_list.filter((role) => role.is_active),
      ),
      isMaterialAudit: this.roleSrv.isMaterialAuditCommitteeRole(
        userInfo.acces_rights_subs ?? [],
      ),
      userPermissionArray:
        userInfo.access_rights_list.filter((role) => role.is_active) ?? [],
      AllUserPermissionArray: userInfo.access_rights_list ?? [],
      materialAuditPermission: userInfo.acces_rights_subs ?? [],
      buildingAuditPermission: userInfo.committee_positions ?? [],
      menu_access: userInfo.menu_access,
      visibleSubMenuIds,
      visibleChildrenSubMenuIds,
      firstDefaultPath: setDefaultPath,
      department_id:
        userInfo.agency_type === DepartmentsCodeEnum.Central ? 0 : null,
      department:
        userInfo.agency_type === DepartmentsCodeEnum.Central
          ? 'ส่วนกลาง'
          : userInfo.department,
      department_1: userInfo.org_item.org_1_is_head
        ? userInfo.department_1
        : '',
    });
  }

  getActionPermissionsData$(): Observable<IActionPermissionManage> {
    return this.http
      .get<IActionPermissionResponse>(this.actionPermissionsUrl)
      .pipe(
        tap((res) => {
          if (res.status_code === StatusCodeEnum.SUCCESS && res.data) {
            this.actionPermissionSubject.next(res.data);
          } else {
            this.actionPermissionSubject.next(null);
          }
        }),
        filter(
          (res) => res.status_code === StatusCodeEnum.SUCCESS && !!res.data,
        ),
        map((res) => res.data),
        catchError((err) => {
          this.actionPermissionSubject.next(null);
          return throwError(() => err);
        }),
      );
  }

  actionPermission(
    managementType: ManagementTypeKey,
    action:
      | GeneralManagementAction
      | AssetManagementAction
      | SystemManagementAction,
  ): boolean {
    const data = this.actionPermissionSubject.value;
    if (!data) return false;

    let currentPermission = !!data[managementType]?.permissions?.[action];
    return currentPermission;
  }

  getVisibleMenuIds(userInfo: IUserLogin) {
    const menus = userInfo.menu_permissions.flatMap(({ menus }) => menus);

    const visibleSubMenuIds = menus
      .filter((menu) => menu.is_view)
      .map((menu) => Number(menu.sub_menu_id));

    const visibleChildrenSubMenuIds = menus
      .filter(
        (menu) =>
          menu.is_view &&
          Array.isArray(menu.child_sub_menus) &&
          menu.child_sub_menus.length > 0,
      )
      .flatMap((menu) => menu.child_sub_menus)
      .filter((child) => child?.is_view)
      .map((cm) => cm?.child_sub_menu_id ?? 0);

    return {
      visibleSubMenuIds,
      visibleChildrenSubMenuIds,
    };
  }

  buildFullName(firstName?: string, lastName?: string): string {
    return `${firstName || '-'} ${lastName || '-'}`;
  }

  getUserToken() {
    let token = sessionStorage.getItem('userToken') || '';
    let decodeToken = this.decodeFromBase64Url(token);
    return decodeToken;
  }

  getAccessToken() {
    let token = sessionStorage.getItem('access_token') || '';
    let decodeToken = this.decodeFromBase64Url(token);
    return decodeToken;
  }

  getUserLogInIdCard() {
    let idCard = sessionStorage.getItem('info') || '';
    return idCard ? this.decodeFromBase64Url(idCard) : '';
  }

  getSystemId() {
    let system_id = sessionStorage.getItem('system_id') || '';
    return system_id;
  }

  removeAccessParam() {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('system_id');
    sessionStorage.removeItem('userToken');
    sessionStorage.removeItem('info');
    sessionStorage.removeItem('userRoleId'); // max
  }

  setUserProfile(userProfile: IUserProfileDetails) {
    this.currentUserLogin.next(userProfile);
  }

  getTabsBySubMenuId(subMenuId: number, visibleChildren: number[]): string[] {
    const childIds = subMenuToChildIdsMap[subMenuId] || [];

    return childIds
      .filter((id) => visibleChildren.includes(id))
      .map((id) => childSubMenuIdMap[id])
      .filter(Boolean);
  }

  findFirstSidebarPathBySubMenuId(
    visibleSubMenuIds: number[],
    visibleChildrenIds: number[],
  ): string {
    const firstSubMenuId = visibleSubMenuIds.at(0);
    const firstChildId = visibleChildrenIds.at(0);

    if (!firstSubMenuId) return '';

    const childSubMenuIds =
      allSubMenusToChildIdsMapRecord[firstSubMenuId] ?? [];

    const mainMenu = sidebarMenus
      .flatMap((menu) => menu.mainMenus)
      .find((menu) => menu.subMenuId === firstSubMenuId);

    if (!mainMenu) {
      const findSideBarNoSubMenu = sidebarMenus.find((s) => s.subMenuId);
      if (findSideBarNoSubMenu && findSideBarNoSubMenu.link) {
        return findSideBarNoSubMenu.link;
      } else {
        return '';
      }
    }

    if (childSubMenuIds.length === 0) {
      return mainMenu.link ?? '';
    }

    if (firstChildId && childSubMenuIds.includes(firstChildId)) {
      const childMenu = mainMenu.subMenus?.find(
        (sm) => sm.childSubMenuId === firstChildId,
      );
      return childMenu?.link ?? '';
    }

    return '';
  }
}
